import { Children, useMemo } from 'react';

import { invariant } from '@e-socna/utils';

import { checkIfIsStepCmpProps } from '../utils/check-if-is-step-cmp-props.util';
import { useWizardContext } from '../contexts/WizardContext/hooks/useWizardContext.hook';
import type { StepCmpProps } from '../types/step-cmp-props.type';

export const useSteps = <V extends object, VS extends object, SE>() => {
  const { props, currentStepId } = useWizardContext<V, VS, SE>();

  const { wizardChildren, steps } = props;

  return useMemo(() => {
    const enabledSteps = steps.filter((step) => step.isEnabled);

    const childrenSteps = Children.toArray(wizardChildren);

    const enabledChildrenSteps = childrenSteps.filter(
      (_, i) => steps[i]?.isEnabled,
    );

    const currentEnabledIndexInEnabledSteps = enabledSteps.findIndex(
      (step) => step.id === currentStepId,
    );

    const currentEnabledIndexInAllSteps = steps.findIndex(
      (step) => step.id === currentStepId,
    );

    const currentEnabledStep = enabledSteps[currentEnabledIndexInEnabledSteps];

    const currentEnabledStepCmp =
      enabledChildrenSteps[currentEnabledIndexInEnabledSteps];

    const totalEnabledSteps = enabledChildrenSteps.length;

    const isLastEnabledStep =
      totalEnabledSteps - 1 === currentEnabledIndexInEnabledSteps;

    const stepsCmpProps: readonly StepCmpProps<V, SE>[] = childrenSteps.map(
      (step) => {
        if (step && typeof step === 'object' && 'props' in step) {
          if (checkIfIsStepCmpProps<V, SE>(step.props)) {
            return step.props;
          }

          throw new Error("Wizard child isn't a valid element");
        }

        throw new Error('Wizard children must be React elements');
      },
    );

    invariant(
      !stepsCmpProps.some(
        (stepCmpProps, index) =>
          stepCmpProps.id !== steps[index]?.id || !stepCmpProps.id,
      ),
      'Wizard children and steps must have the same order of ids',
    );

    const enabledStepsCmpProps = stepsCmpProps.filter(
      (_, i) => steps[i]?.isEnabled,
    );

    const currentEnabledStepCmpProps =
      enabledStepsCmpProps[currentEnabledIndexInEnabledSteps];

    return {
      enabledSteps,
      currentEnabledIndexInEnabledSteps,
      currentEnabledStepCmpProps,
      isLastEnabledStep,
      currentEnabledIndexInAllSteps,
      currentEnabledStep,
      currentEnabledStepCmp,
      childrenSteps,
    };
  }, [wizardChildren, currentStepId, steps]);
};
